import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, Trash2, Edit3, AlertOctagon, CheckCircle2, BarChart3, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { formatRomeDateTime } from '../lib/datetime';

const REASON_LABELS = {
  guasto: 'Guasto',
  ispezione: 'Ispezione',
  blackout: 'Blackout/Emergenza',
};

const REASON_COLORS = {
  guasto: 'bg-red-100 text-red-800 border-red-300',
  ispezione: 'bg-amber-100 text-amber-800 border-amber-300',
  blackout: 'bg-purple-100 text-purple-800 border-purple-300',
};

const ElevatorsPage = () => {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'sede'].includes(user?.role);
  const canOpenBlock = ['super_admin', 'hotel_manager', 'frontoffice', 'sede'].includes(user?.role);

  const [tab, setTab] = useState(isAdmin ? 'impianti' : 'blocchi');
  const [elevators, setElevators] = useState([]);
  const [blockages, setBlockages] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);

  // Elevator CRUD dialog
  const [elevDialog, setElevDialog] = useState(false);
  const [editingElev, setEditingElev] = useState(null);
  const [elevForm, setElevForm] = useState({ hotel_id: '', number: '', description: '', notes: '' });
  const [elevSubmitting, setElevSubmitting] = useState(false);

  // Block dialog
  const [blockDialog, setBlockDialog] = useState(false);
  const [blockForm, setBlockForm] = useState({
    elevator_id: '',
    blocked_at: new Date().toISOString().slice(0, 16),
    reason: 'guasto',
    notes: '',
  });
  const [blockSubmitting, setBlockSubmitting] = useState(false);

  // Close dialog
  const [closeDialog, setCloseDialog] = useState(false);
  const [closingBlockage, setClosingBlockage] = useState(null);
  const [closingNotes, setClosingNotes] = useState('');
  const [closeSubmitting, setCloseSubmitting] = useState(false);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [elevRes, blockRes, hotelsRes] = await Promise.all([
        api.get('/elevators'),
        api.get('/elevator-blockages?limit=200'),
        api.get('/hotels'),
      ]);
      setElevators(elevRes.data);
      setBlockages(blockRes.data);
      setHotels(hotelsRes.data);
    } catch (e) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // ===== Elevator CRUD =====
  const openElevDialog = (elev = null) => {
    if (elev) {
      setEditingElev(elev);
      setElevForm({
        hotel_id: elev.hotel_id || '',
        number: elev.number || '',
        description: elev.description || '',
        notes: elev.notes || '',
      });
    } else {
      setEditingElev(null);
      setElevForm({ hotel_id: hotels[0]?.id || '', number: '', description: '', notes: '' });
    }
    setElevDialog(true);
  };

  const submitElev = async (e) => {
    e.preventDefault();
    if (elevSubmitting) return;
    if (!elevForm.hotel_id || !elevForm.number) {
      toast.error('Hotel e numero impianto sono obbligatori');
      return;
    }
    try {
      setElevSubmitting(true);
      if (editingElev) {
        await api.put(`/elevators/${editingElev.id}`, {
          number: elevForm.number,
          description: elevForm.description,
          notes: elevForm.notes,
        });
        toast.success('Impianto aggiornato');
      } else {
        await api.post('/elevators', elevForm);
        toast.success('Impianto creato');
      }
      setElevDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setElevSubmitting(false);
    }
  };

  const deleteElev = async (elev) => {
    if (!window.confirm(`Disattivare l'ascensore "${elev.number}" dell'hotel ${elev.hotel_name}?`)) return;
    try {
      await api.delete(`/elevators/${elev.id}`);
      toast.success('Impianto disattivato');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  // ===== Blockage flow =====
  const openBlockDialog = () => {
    const activeElev = elevators.filter(e => !e.is_blocked);
    if (activeElev.length === 0) {
      toast.error('Nessun impianto disponibile per aprire un blocco');
      return;
    }
    setBlockForm({
      elevator_id: activeElev[0].id,
      blocked_at: new Date().toISOString().slice(0, 16),
      reason: 'guasto',
      notes: '',
    });
    setBlockDialog(true);
  };

  const submitBlock = async (e) => {
    e.preventDefault();
    if (blockSubmitting) return;
    if (!blockForm.elevator_id || !blockForm.reason) {
      toast.error('Seleziona impianto e motivo');
      return;
    }
    try {
      setBlockSubmitting(true);
      const blockedAtISO = new Date(blockForm.blocked_at).toISOString();
      await api.post('/elevator-blockages', { ...blockForm, blocked_at: blockedAtISO });
      toast.success('Blocco aperto');
      setBlockDialog(false);
      setTab('blocchi');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setBlockSubmitting(false);
    }
  };

  const openCloseDialog = (b) => {
    setClosingBlockage(b);
    setClosingNotes('');
    setCloseDialog(true);
  };

  const submitClose = async () => {
    if (closeSubmitting || !closingBlockage) return;
    try {
      setCloseSubmitting(true);
      const res = await api.put(`/elevator-blockages/${closingBlockage.id}/close`, { closing_notes: closingNotes });
      toast.success(`Blocco chiuso. Durata: ${res.data.duration_hours}h`);
      setCloseDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setCloseSubmitting(false);
    }
  };

  // ===== Helpers =====
  const canCloseBlockage = (b) => {
    if (b.status !== 'in_blocco') return false;
    if (b.created_by === user?.id) return true;
    if (['super_admin', 'sede'].includes(user?.role)) return true;
    if (user?.role === 'hotel_manager' && (user?.assigned_hotels || []).includes(b.hotel_id)) return true;
    return false;
  };

  const activeBlocks = blockages.filter(b => b.status === 'in_blocco');
  const resolvedBlocks = blockages.filter(b => b.status === 'risolto');

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="elevators-loading">Caricamento...</div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="elevators-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Outfit, sans-serif' }}>
            🛗 Ascensori
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Gestione impianti e tracciamento blocchi</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(isAdmin || user?.role === 'hotel_manager') && (
            <Link to="/ascensori/statistiche" className="flex-1 sm:flex-none">
              <Button variant="outline" className="w-full sm:w-auto" data-testid="elevator-stats-link">
                <BarChart3 className="h-4 w-4 mr-2" />
                Statistiche
                <ArrowUpRight className="h-3 w-3 ml-1" />
              </Button>
            </Link>
          )}
          {canOpenBlock && (
            <Button
              onClick={openBlockDialog}
              className="bg-red-600 hover:bg-red-700 text-white flex-1 sm:flex-none"
              data-testid="open-blockage-button"
            >
              <AlertOctagon className="h-4 w-4 mr-2" />
              Apri Blocco
            </Button>
          )}
        </div>
      </div>

      {/* Quick KPIs */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
        <Card className="p-3 sm:p-4 border-l-4 border-l-red-600 dark:bg-slate-900" data-testid="kpi-currently-blocked">
          <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">In blocco ora</div>
          <div className="text-2xl sm:text-3xl font-bold text-red-600 mt-1">{activeBlocks.length}</div>
        </Card>
        <Card className="p-3 sm:p-4 border-l-4 border-l-blue-600 dark:bg-slate-900">
          <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Impianti totali</div>
          <div className="text-2xl sm:text-3xl font-bold text-blue-700 dark:text-blue-400 mt-1">{elevators.length}</div>
        </Card>
        <Card className="p-3 sm:p-4 border-l-4 border-l-green-600 dark:bg-slate-900">
          <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Risolti</div>
          <div className="text-2xl sm:text-3xl font-bold text-green-700 dark:text-green-400 mt-1">{resolvedBlocks.length}</div>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full sm:w-auto grid grid-cols-2 sm:flex">
          {isAdmin && <TabsTrigger value="impianti" data-testid="tab-impianti">Impianti</TabsTrigger>}
          <TabsTrigger value="blocchi" data-testid="tab-blocchi">
            Eventi Blocco {activeBlocks.length > 0 && (
              <Badge className="ml-2 bg-red-600 text-white">{activeBlocks.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* === TAB IMPIANTI === */}
        {isAdmin && (
          <TabsContent value="impianti" className="mt-4">
            <Card className="p-3 sm:p-4 dark:bg-slate-900">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <h2 className="text-lg font-semibold dark:text-slate-100">Anagrafica impianti</h2>
                <Button onClick={() => openElevDialog()} data-testid="new-elevator-button" className="w-full sm:w-auto">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuovo Impianto
                </Button>
              </div>
              {elevators.length === 0 ? (
                <p className="text-sm text-slate-500 py-8 text-center">Nessun impianto registrato. Clicca su &quot;Nuovo Impianto&quot; per aggiungerne uno.</p>
              ) : (
                <div className="-mx-3 sm:mx-0 overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead className="text-left text-slate-500 dark:text-slate-400 border-b dark:border-slate-700">
                      <tr>
                        <th className="py-2 px-3 whitespace-nowrap">Hotel</th>
                        <th className="py-2 px-3 whitespace-nowrap">N° Impianto</th>
                        <th className="py-2 px-3">Descrizione</th>
                        <th className="py-2 px-3 whitespace-nowrap">Stato</th>
                        <th className="py-2 px-3 text-right whitespace-nowrap">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {elevators.map(e => (
                        <tr key={e.id} className="border-b dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:text-slate-200" data-testid={`elevator-row-${e.id}`}>
                          <td className="py-2 px-3 font-medium">{e.hotel_name}</td>
                          <td className="py-2 px-3 font-mono">{e.number}</td>
                          <td className="py-2 px-3 text-slate-600 dark:text-slate-400">{e.description || '—'}</td>
                          <td className="py-2 px-3 whitespace-nowrap">
                            {e.is_blocked ? (
                              <Badge className="bg-red-600 text-white">In blocco</Badge>
                            ) : (
                              <Badge variant="outline" className="text-green-700 border-green-300 dark:text-green-400">Operativo</Badge>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right whitespace-nowrap">
                            <Button variant="ghost" size="sm" onClick={() => openElevDialog(e)} data-testid={`edit-elevator-${e.id}`}>
                              <Edit3 className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteElev(e)} data-testid={`delete-elevator-${e.id}`}>
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </TabsContent>
        )}

        {/* === TAB BLOCCHI === */}
        <TabsContent value="blocchi" className="mt-4 space-y-4">
          {activeBlocks.length > 0 && (
            <Card className="p-3 sm:p-4 border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900">
              <h2 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-3">⚠️ Blocchi attivi</h2>
              <div className="space-y-2">
                {activeBlocks.map(b => (
                  <div key={b.id} className="bg-white dark:bg-slate-900 p-3 rounded border border-red-200 dark:border-red-900 flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3" data-testid={`active-blockage-${b.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`border ${REASON_COLORS[b.reason] || ''}`}>{REASON_LABELS[b.reason] || b.reason}</Badge>
                        <span className="font-semibold dark:text-slate-100">{b.hotel_name} — Ascensore {b.elevator_number}</span>
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        Bloccato il {formatRomeDateTime(b.blocked_at)} da {b.created_by_name || '—'}
                      </div>
                      {b.notes && <div className="text-sm text-slate-700 dark:text-slate-300 mt-1 break-words">{b.notes}</div>}
                    </div>
                    {canCloseBlockage(b) && (
                      <Button onClick={() => openCloseDialog(b)} className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto" data-testid={`close-blockage-${b.id}`}>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Chiudi blocco
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="p-3 sm:p-4 dark:bg-slate-900">
            <h2 className="text-lg font-semibold mb-3 dark:text-slate-100">Cronologia blocchi</h2>
            {resolvedBlocks.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">Nessun blocco storico</p>
            ) : (
              <div className="-mx-3 sm:mx-0 overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="text-left text-slate-500 dark:text-slate-400 border-b dark:border-slate-700">
                    <tr>
                      <th className="py-2 px-3 whitespace-nowrap">Hotel</th>
                      <th className="py-2 px-3 whitespace-nowrap">Asc.</th>
                      <th className="py-2 px-3 whitespace-nowrap">Motivo</th>
                      <th className="py-2 px-3 whitespace-nowrap">Inizio</th>
                      <th className="py-2 px-3 whitespace-nowrap">Fine</th>
                      <th className="py-2 px-3 text-right whitespace-nowrap">Durata</th>
                      <th className="py-2 px-3 whitespace-nowrap">Chiuso da</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resolvedBlocks.map(b => (
                      <tr key={b.id} className="border-b dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:text-slate-200" data-testid={`resolved-blockage-${b.id}`}>
                        <td className="py-2 px-3">{b.hotel_name}</td>
                        <td className="py-2 px-3 font-mono">{b.elevator_number}</td>
                        <td className="py-2 px-3">
                          <Badge variant="outline" className={REASON_COLORS[b.reason] || ''}>{REASON_LABELS[b.reason] || b.reason}</Badge>
                        </td>
                        <td className="py-2 px-3 text-xs whitespace-nowrap">{formatRomeDateTime(b.blocked_at)}</td>
                        <td className="py-2 px-3 text-xs whitespace-nowrap">{formatRomeDateTime(b.unblocked_at)}</td>
                        <td className="py-2 px-3 text-right font-mono font-semibold whitespace-nowrap">{b.duration_hours?.toFixed(1) || '—'}h</td>
                        <td className="py-2 px-3 whitespace-nowrap">{b.closed_by_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* ===== Elevator CRUD Dialog ===== */}
      <Dialog open={elevDialog} onOpenChange={setElevDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingElev ? 'Modifica impianto' : 'Nuovo impianto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitElev} className="space-y-3" data-testid="elevator-form">
            <div>
              <Label>Hotel *</Label>
              <Select value={elevForm.hotel_id} onValueChange={v => setElevForm(s => ({ ...s, hotel_id: v }))} disabled={!!editingElev}>
                <SelectTrigger data-testid="elevator-hotel-select"><SelectValue placeholder="Seleziona hotel" /></SelectTrigger>
                <SelectContent>
                  {hotels.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>N° Impianto *</Label>
              <Input value={elevForm.number} onChange={e => setElevForm(s => ({ ...s, number: e.target.value }))} placeholder='es. "Asc. 1" o "A-Spa"' data-testid="elevator-number-input" />
            </div>
            <div>
              <Label>Descrizione</Label>
              <Input value={elevForm.description} onChange={e => setElevForm(s => ({ ...s, description: e.target.value }))} placeholder="es. 8 piani, 6 persone" />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea rows={2} value={elevForm.notes} onChange={e => setElevForm(s => ({ ...s, notes: e.target.value }))} placeholder="Note tecniche, manuale ditta installatrice..." />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setElevDialog(false)} disabled={elevSubmitting}>Annulla</Button>
              <Button type="submit" disabled={elevSubmitting} data-testid="elevator-submit-button">
                {elevSubmitting ? 'Salvataggio...' : 'Salva'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== Open Blockage Dialog ===== */}
      <Dialog open={blockDialog} onOpenChange={setBlockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-700">🚨 Apri blocco ascensore</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitBlock} className="space-y-3" data-testid="blockage-form">
            <div>
              <Label>Ascensore *</Label>
              <Select value={blockForm.elevator_id} onValueChange={v => setBlockForm(s => ({ ...s, elevator_id: v }))}>
                <SelectTrigger data-testid="blockage-elevator-select"><SelectValue placeholder="Seleziona impianto" /></SelectTrigger>
                <SelectContent>
                  {elevators.filter(e => !e.is_blocked).map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.hotel_name} — {e.number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data/ora inizio blocco *</Label>
              <Input
                type="datetime-local"
                value={blockForm.blocked_at}
                onChange={e => setBlockForm(s => ({ ...s, blocked_at: e.target.value }))}
                data-testid="blockage-datetime-input"
              />
            </div>
            <div>
              <Label>Motivo *</Label>
              <Select value={blockForm.reason} onValueChange={v => setBlockForm(s => ({ ...s, reason: v }))}>
                <SelectTrigger data-testid="blockage-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="guasto">Guasto</SelectItem>
                  <SelectItem value="ispezione">Ispezione</SelectItem>
                  <SelectItem value="blackout">Blackout/Emergenza</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note sul blocco</Label>
              <Textarea
                rows={3}
                value={blockForm.notes}
                onChange={e => setBlockForm(s => ({ ...s, notes: e.target.value }))}
                placeholder="Descrivi il problema, eventuali ditte contattate..."
                data-testid="blockage-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBlockDialog(false)} disabled={blockSubmitting}>Annulla</Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white" disabled={blockSubmitting} data-testid="blockage-submit-button">
                {blockSubmitting ? 'Apertura...' : 'Apri Blocco'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== Close Blockage Dialog ===== */}
      <Dialog open={closeDialog} onOpenChange={setCloseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-green-700">Chiudi blocco</DialogTitle>
          </DialogHeader>
          {closingBlockage && (
            <div className="space-y-3">
              <div className="bg-slate-50 p-3 rounded text-sm">
                <div><span className="text-slate-500">Hotel:</span> <span className="font-medium">{closingBlockage.hotel_name}</span></div>
                <div><span className="text-slate-500">Ascensore:</span> <span className="font-medium">{closingBlockage.elevator_number}</span></div>
                <div><span className="text-slate-500">Aperto:</span> {formatRomeDateTime(closingBlockage.blocked_at)}</div>
                <div className="text-xs text-slate-500 mt-1">La durata sarà calcolata automaticamente sulla data/ora attuale.</div>
              </div>
              <div>
                <Label>Note di chiusura (opzionale)</Label>
                <Textarea
                  rows={3}
                  value={closingNotes}
                  onChange={e => setClosingNotes(e.target.value)}
                  placeholder="Cosa è stato fatto per risolvere..."
                  data-testid="closing-notes-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCloseDialog(false)} disabled={closeSubmitting}>Annulla</Button>
                <Button onClick={submitClose} className="bg-green-600 hover:bg-green-700 text-white" disabled={closeSubmitting} data-testid="close-submit-button">
                  {closeSubmitting ? 'Chiusura...' : 'Conferma chiusura'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ElevatorsPage;
